import * as React from "react";
import { Image, X } from "lucide-react";
import { DocumentDialog } from "../ui/DocumentDialog";
import { AppButton } from "../ui/AppButton";

interface DocumentPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  documentTitle: string;
  previewUrl: string;
  isPdfPreview?: boolean;
  previewError?: boolean;
  onPreviewError?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
}

export function DocumentPreviewDialog({
  open,
  onOpenChange,
  title,
  subtitle,
  documentTitle,
  previewUrl,
  isPdfPreview = false,
  previewError = false,
  onPreviewError,
  onDownload,
}: DocumentPreviewDialogProps) {
  return (
    <DocumentDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      subtitle={subtitle}
      submitText="Download"
      onSubmit={onDownload}
      divider={false}
      styles={{
        maxWidth: "860px",
        width: "100%",
        height: "auto",
        margin: "auto",
      }}
    >
      {/* Close Icon */}
      <button
        onClick={() => onOpenChange(false)}
        className="absolute top-4 right-4 p-1 rounded-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label="Close dialog"
      >
        <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
      </button>

      {/* Document Header */}
      <div className="mb-3">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          {documentTitle}
        </h3>
      </div>

      {/* Preview Section */}
      <div className="flex justify-center items-center bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
        {previewError ? (
          <div className="text-center p-8 w-full">
            <div className="flex justify-center mb-4">
              <Image className="h-12 w-12 text-gray-400" />
            </div>
            <p className="text-red-500 mb-2">Unable to preview document</p>
            <p className="text-sm text-gray-500">
              The document may be corrupted or in an unsupported format.
            </p>
          </div>
        ) : isPdfPreview ? (
          <iframe
            src={previewUrl}
            width="100%"
            height="600px"
            title="PDF Preview"
            onError={onPreviewError}
            className="border-0 rounded-md"
          />
        ) : (
          <div className="flex justify-center items-center p-4">
            <img
              src={previewUrl}
              alt="Document Preview"
              onError={onPreviewError}
              className="max-w-full max-h-[550px] object-contain rounded shadow-sm"
            />
          </div>
        )}
      </div>
    </DocumentDialog>
  );
}